import { type PlatformContract, type PublishPayload, safeWebUrl } from "@ondit/publishing";
import { pick, useI18n, useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { Markdown } from "~/modules/analytics/components/markdown";
import "~/modules/analytics/features/platform-publishing/publishing.css";

export function PlatformPreview({
	platform,
	payload,
	externalHeading = false,
}: {
	platform: PlatformContract;
	payload: PublishPayload;
	externalHeading?: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	const display = platform.preview.fields.filter(
		(f) => !["body", "title", "destination"].includes(f.key) && payload.fields[f.key],
	);
	return (
		<section
			className={`platform-preview platform-preview--${platform.preview.layout}`}
			data-platform={platform.id}
			aria-label={`${platform.name} ${t("publishing.platform.preview")}`}
		>
			<header className="record-heading">
				<strong>{platform.name}</strong>
				{!externalHeading && (
					<span>
						{t("publishing.platform.preview")} <Hint text={t("publishing.platform.previewHelp")} />
					</span>
				)}
			</header>
			{payload.fields.destination && <p className="hint">{payload.fields.destination}</p>}
			{payload.fields.title && <h3>{payload.fields.title}</h3>}
			{platform.preview.media && (platform.preview.media.required || payload.media.length > 0) && (
				<div className="platform-preview-media">
					{payload.media.length ? (
						payload.media.map((m) => (
							<figure key={m.url}>
								{safeWebUrl(m.url) &&
									(/\.(mp4|webm|mov)(?:[?#]|$)/i.test(m.url) ? (
										<video
											muted
											src={m.url}
											controls
											preload="metadata"
											aria-label={m.alt || t("publishing.platform.assets")}
										/>
									) : (
										<img src={m.url} alt={m.alt} loading="lazy" referrerPolicy="no-referrer" />
									))}
								<a href={m.url} target="_blank" rel="noreferrer">
									{m.alt || m.url}
								</a>
							</figure>
						))
					) : (
						<p className="hint">
							{t(platform.preview.media.required ? "publishing.platform.mediaRequired" : "publishing.platform.assets")}
						</p>
					)}
				</div>
			)}
			<div className="platform-preview-body">
				{["article", "newsletter", "discussion"].includes(platform.preview.layout) ? (
					<Markdown text={payload.fields.body ?? ""} />
				) : (
					<p className="platform-plain-text">{payload.fields.body}</p>
				)}
			</div>
			{display.length > 0 && (
				<dl className="platform-preview-settings">
					{display.map((f) => (
						<div key={f.key}>
							<dt>{pick(locale, f.label)}</dt>
							<dd>{pick(locale, f.option_labels?.[payload.fields[f.key]]) ?? payload.fields[f.key]}</dd>
						</div>
					))}
				</dl>
			)}
		</section>
	);
}
